// Sugalarm — Cycle 74b: map a free-text meal note to a food ICON key
// for the Now-screen IOB meal lines. Keyword match only — no model
// call, no category picker (out of scope).
//
// Cycle 74b.1: the icons are rendered as inline SVG (see
// `FoodGlyph.tsx`), NOT emoji. The kiosk runs Chromium under `cage`
// without a color-emoji font (Noto Color Emoji is not installed), so
// emoji glyphs painted as blank tofu rectangles there. Inline SVG
// renders identically on every surface regardless of installed fonts.
//
// Precedence matters where keywords overlap: the FIRST entry whose
// keyword list matches the lowercased note wins. Compound / more
// specific terms ("ice cream", "burrito") are listed before generic
// ones. Substrings that could collide ("rice" vs "ice cream") don't,
// because each is matched as its own whole keyword via `includes`, and
// neither is a substring of the other.

export type FoodIconKey =
  | "pizza"
  | "pancake"
  | "noodle"
  | "taco"
  | "burger"
  | "sandwich"
  | "fries"
  | "dessert"
  | "rice"
  | "salad"
  | "egg"
  | "chicken"
  | "soup"
  | "cereal"
  | "coffee"
  | "fruit"
  | "meal"; // generic fallback (fork + knife)

export const FOOD_FALLBACK_KEY: FoodIconKey = "meal";

// Ordered: first match wins.
const TABLE: ReadonlyArray<readonly [readonly string[], FoodIconKey]> = [
  [["pizza"], "pizza"],
  [["pancake", "waffle"], "pancake"],
  [["spaghetti", "pasta", "noodle", "ramen"], "noodle"],
  [["burrito", "taco"], "taco"],
  [["burger", "cheeseburger"], "burger"],
  [["sandwich", "toast", "bread"], "sandwich"],
  [["fries", "french fr"], "fries"],
  [["ice cream", "icecream", "dessert"], "dessert"],
  [["rice"], "rice"],
  [["salad"], "salad"],
  [["egg"], "egg"],
  [["chicken"], "chicken"],
  [["soup"], "soup"],
  [["cereal"], "cereal"],
  [["coffee"], "coffee"],
  [["fruit", "apple"], "fruit"],
];

/** Resolve a meal note to a food-icon key; the generic "meal"
 *  (fork + knife) key when no keyword matches or the note is empty. */
export function foodIconKey(notes: string | null | undefined): FoodIconKey {
  if (!notes) return FOOD_FALLBACK_KEY;
  const n = notes.toLowerCase();
  for (const [keywords, key] of TABLE) {
    if (keywords.some((k) => n.includes(k))) return key;
  }
  return FOOD_FALLBACK_KEY;
}

/** True when the note matches a known food keyword — used by
 *  `isCorrectionBolus` to treat a recognized food note as a meal. */
export function matchesFoodKeyword(notes: string | null | undefined): boolean {
  if (!notes) return false;
  const n = notes.toLowerCase();
  return TABLE.some(([keywords]) => keywords.some((k) => n.includes(k)));
}
